import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { myServices } from '../../../services/services.connected.app';
import { rd } from '@passionware/monads';
import { Production } from '../../../../__types__';
import { CallsheetDetailsFields } from '../_shared/CallsheetDetailsFields';
import { ProductionResourcesPreview } from '../_shared/ProductionResourcesPreview';
import { useProductionResourceCounts } from '../_shared/useProductionResourceCounts';
import type { CallsheetFormData } from '../types';

export interface CallsheetDetailsStepProps {
  form: UseFormReturn<CallsheetFormData>;
}

/**
 * Callsheet Details Step - Second step in callsheet creation
 *
 * Allows users to enter callsheet title, date, and general call time,
 * with a preview of selected productions and their resources.
 */
export function CallsheetDetailsStep({ form }: CallsheetDetailsStepProps) {
  const selectedProductionIds = form.watch('selectedProductionIds');
  const dateSelection = form.watch('dateSelection');
  const generalCallTime = form.watch('generalCallTime');

  // Auto-focus the title field when the step is shown
  useEffect(() => {
    // Use requestAnimationFrame to ensure the DOM is ready
    requestAnimationFrame(() => {
      form.setFocus('title');
    });
  }, [form]);

  // Get workspace ID
  const app_id = useSelector((state: any) => state?.auth?.app_id);

  // Fetch selected productions by IDs
  const selectedProductionsRecord =
    myServices.productionService.useProductionsById(
      app_id,
      selectedProductionIds
    );

  // Extract productions from record, filtering out non-success states
  const selectedProductions = useMemo(() => {
    return selectedProductionIds
      .map(id => {
        const productionData = selectedProductionsRecord[id];
        return rd.tryGet(productionData);
      })
      .filter((p): p is Production => p !== undefined);
  }, [selectedProductionsRecord, selectedProductionIds]);

  // Get resource counts using shared hook
  const resourceCount = useProductionResourceCounts({
    productionsRecord: selectedProductionsRecord,
    dateSelection,
    generalCallTime
  });

  return (
    <>
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-xl font-semibold">Callsheet details</h2>
      </div>

      {/* Form Fields */}
      <CallsheetDetailsFields form={form} />

      {/* Resources Section */}
      <ProductionResourcesPreview
        productions={selectedProductions}
        resourceCount={resourceCount}
        title="Add resources from productions"
        description="Resources booked on the selected date will be added into the callsheet."
        emptyMessage="No productions selected"
        onReorder={ids =>
          form.setValue('selectedProductionIds', ids, { shouldDirty: true })
        }
      />
    </>
  );
}
